import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { fromUnixTime } from "date-fns/esm";

import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import { WorkerUsage, WorkerUsageCycle } from "types/generated";

import UsageWorkersContent from "./Content";
import UsageWorkersHeader from "./Header";
import { GET_WORKER_USAGE } from "./gql";
import { FALLBACK_PERCENTILE, REQUESTED_PERCENTILES } from "./constants";
import { WorkerUsageData, WorkerUsageSelectableCycle } from "./types";
import { startOfUtcDay } from "./utils";

const clampToCycle = (date: Date, start: Date, end: Date) =>
  new Date(Math.min(Math.max(date.getTime(), start.getTime()), end.getTime()));

const normalizeWorkerUsageCycle = (cycle: WorkerUsageCycle): WorkerUsageSelectableCycle => ({
  cyclesAgo: cycle.cyclesAgo,
  start: fromUnixTime(cycle.start),
  // cycle.end is exclusive, so the last day is the one holding its last billed minute.
  end: startOfUtcDay(fromUnixTime(cycle.end - 1)),
});

const normalizeWorkerUsage = (usage: WorkerUsage): WorkerUsageData => {
  const { start: cycleStart, end: cycleEnd } = normalizeWorkerUsageCycle(usage.cycle);

  // Half a contract renders exactly like no contract, so the pick happens once,
  // here: the rest of the page sees a single percentile series and never asks
  // which one it is.
  const hasWholeContract =
    usage.contractedLimit != null &&
    usage.contractedPercentile != null &&
    usage.concurrencyAtContractedPercentile != null;

  return {
    cyclesAgo: usage.cycle.cyclesAgo,
    selectableCycles: usage.selectableCycles.map(normalizeWorkerUsageCycle),
    cycleStart,
    cycleEnd,
    today: clampToCycle(startOfUtcDay(new Date()), cycleStart, cycleEnd),
    daily: usage.daily.map((point) => ({
      day: fromUnixTime(point.day),
      max: point.max,
      percentileToDateValue: hasWholeContract
        ? (point.concurrencyAtContractedPercentileToDate ?? undefined)
        : point.concurrencyAtPercentilesToDate.find(
            (entry) => entry.percentile === FALLBACK_PERCENTILE
          )?.value,
      burstMinutesUsed: point.burstMinutesUsed ?? undefined,
    })),
    contractedWorkers: usage.contractedLimit ?? undefined,
    contractedPercentile: usage.contractedPercentile ?? undefined,
    percentileToDateValue: usage.concurrencyAtContractedPercentile ?? undefined,
    burst: usage.burst ?? undefined,
    fallbackPercentile: FALLBACK_PERCENTILE,
    fallbackPercentileValue: usage.concurrencyPercentiles.find(
      (entry) => entry.percentile === FALLBACK_PERCENTILE
    )?.value,
    multiAccountContract: usage.multiAccountContract,
  };
};

const UsageWorkers = () => {
  const [cyclesAgo, setCyclesAgo] = useState(0);

  const { loading, data, previousData, error } = useQuery(GET_WORKER_USAGE, {
    variables: { percentiles: REQUESTED_PERCENTILES, input: { cyclesAgo } },
  });

  const usage = useMemo(
    () => (data?.workerUsage ? normalizeWorkerUsage(data.workerUsage) : undefined),
    [data]
  );

  const previousUsage = useMemo(
    () => (previousData?.workerUsage ? normalizeWorkerUsage(previousData.workerUsage) : undefined),
    [previousData]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isLoadingCycle = loading && !usage;

  if (isLoadingCycle && !previousUsage) {
    return <PageLoading />;
  }

  return (
    <>
      <UsageWorkersHeader
        usage={usage ?? previousUsage}
        cyclesAgo={cyclesAgo}
        onCycleChange={setCyclesAgo}
      />

      {isLoadingCycle && <PageLoading />}
      {!isLoadingCycle && <UsageWorkersContent usage={usage} />}
    </>
  );
};

export default UsageWorkers;
